import { useLayoutEffect, useState } from "react";

import type { CornerRadius, SpotlightPadding } from "./configs/types";
import {
  buildRoundedRectPath,
  isUniform,
  resolveCorners
} from "./utils/roundedRectPath";

const PADDING = 6;

type ResolvedPadding = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

/**
 * Normalizes the flexible `targetPadding` prop into explicit per-side values.
 * A number applies to all sides; otherwise each side falls back to its axis
 * shorthand (`x` → left/right, `y` → top/bottom), then to 0.
 */
function resolvePadding(padding: number | SpotlightPadding): ResolvedPadding {
  if (typeof padding === "number") {
    return { top: padding, right: padding, bottom: padding, left: padding };
  }
  return {
    top: padding.top ?? padding.y ?? 0,
    right: padding.right ?? padding.x ?? 0,
    bottom: padding.bottom ?? padding.y ?? 0,
    left: padding.left ?? padding.x ?? 0
  };
}

type Props = {
  targetEl: Element | null;
  measurementKey?: string;
  targetPadding?: number | SpotlightPadding;
  targetRadius?: number | CornerRadius;
};

type RectState = {
  targetEl: Element;
  measurementKey: string;
  rect: DOMRect;
};

const DEFAULT_PADDED_RADIUS = 12;

/**
 * Full-screen SVG mask. The mask has a "hole" cut out over the target,
 * dimming everything else. When `targetEl` is null (action-driven step
 * waiting for an element), the mask covers the full viewport — signalling
 * to the user that an action is expected.
 */
export function TourSpotlight({
  targetEl,
  measurementKey: externalMeasurementKey,
  targetPadding = PADDING,
  targetRadius
}: Props) {
  const [rectState, setRectState] = useState<RectState | null>(null);

  const pad = resolvePadding(targetPadding);
  const hasPadding =
    pad.top !== 0 || pad.right !== 0 || pad.bottom !== 0 || pad.left !== 0;
  const corners = resolveCorners(
    targetRadius,
    hasPadding ? DEFAULT_PADDED_RADIUS : 0
  );
  const measurementKey = [
    externalMeasurementKey,
    pad.top,
    pad.right,
    pad.bottom,
    pad.left
  ].join(":");
  const rect =
    rectState?.targetEl === targetEl &&
    rectState.measurementKey === measurementKey
      ? rectState.rect
      : null;

  useLayoutEffect(() => {
    if (!targetEl) {
      setRectState(null);
      return;
    }
    const updateRect = () =>
      setRectState({
        targetEl,
        measurementKey,
        rect: targetEl.getBoundingClientRect()
      });
    let animationFrameId: number | null = null;

    const updateRectAfterFrame = () => {
      animationFrameId = window.requestAnimationFrame(updateRect);
    };

    // Tour-owned dialogs disable their entrance animation, so the rect is
    // stable by the next frame without waiting on arbitrary DOM animations.
    updateRectAfterFrame();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(targetEl);

    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
      resizeObserver.disconnect();
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [measurementKey, targetEl]);

  return (
    <svg
      data-testid="tour-spotlight"
      className="pointer-events-none fixed inset-0 z-[1011] h-screen w-screen"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <mask id="tour-spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          {rect &&
            (() => {
              const x = rect.left - pad.left;
              const y = rect.top - pad.top;
              const width = rect.width + pad.left + pad.right;
              const height = rect.height + pad.top + pad.bottom;

              // Uniform corners → a plain <rect rx> keeps the markup simple and
              // its x/y/width/height/rx attributes assertable in tests. Mixed
              // corners need a <path> for CSS-style per-corner radii.
              return isUniform(corners) ? (
                <rect
                  data-testid="tour-spotlight-hole"
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  rx={corners.tl}
                  fill="black"
                />
              ) : (
                <path
                  data-testid="tour-spotlight-hole"
                  d={buildRoundedRectPath(x, y, width, height, corners)}
                  fill="black"
                />
              );
            })()}
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="rgba(0,0,0,0.55)"
        mask="url(#tour-spotlight-mask)"
      />
    </svg>
  );
}
